import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { IPeriodicTasks } from '@/modules/periodicTasks/types';
import axiosClient from '@/shared/lib/axios';

// ************************************ Plots *************************************
const getPlotsByEntity = async (entityId?: number, page = 1) => {
  const { data } =
    await axiosClient.get<IPeriodicTasks.IGetPlotsByEntityResponse>(
      `company/portalInsights/entity-Plots/${entityId}`,
      {
        params: { page },
      }
    );
  return data;
};

export const useGetPlotsByEntity = (entityId?: number) => {
  return useInfiniteQuery({
    queryKey: ['getPlotsByEntity', entityId],
    queryFn: ({ pageParam = 1 }) => getPlotsByEntity(entityId, pageParam),
    getNextPageParam: (lastPage, allPages) => {
      const pagesCount = allPages.length;
      const maxPage = lastPage.totalPages;
      return pagesCount < maxPage ? pagesCount + 1 : undefined;
    },
    enabled: Boolean(entityId),
  });
};
// ************************************ Plots *************************************

// ************************************ Task Types ************************************************
export const getJobCategories = async (): Promise<{
  items: IPeriodicTasks.IJobCategory[];
}> => {
  const { data } = await axiosClient.get('/inventory/api/v2/job-categories', {
    params: {
      paginated: false,
    },
  });
  return data;
};

export const useGetJobCategories = () => {
  return useQuery({
    queryKey: ['getJobCategories'],
    queryFn: getJobCategories,
  });
};
/************************************ PERIODIC OPERATION WIDGETS *********************************************/
export const getPeriodicOperationWidgets = async ({
  entityId,
  paramsString,
}: IPeriodicTasks.IPeriodicOperationParamsProps): Promise<IPeriodicTasks.IPeriodicOperationWidgetsProps> => {
  const { data } = await axiosClient.get(
    `/inventory/api/v1/periodic-operation/widgets/entity/${entityId}${paramsString}`
  );
  return data;
};

export const useGetPeriodicOperationWidgets = (
  params: IPeriodicTasks.IPeriodicOperationParamsProps
) => {
  return useQuery({
    queryKey: ['getPeriodicOperationWidgets', params],
    queryFn: () => getPeriodicOperationWidgets(params),
    enabled: !!params.entityId,
  });
};
/************************************ PERIODIC OPERATION TABLE *********************************************/
export const getPeriodicOperationTable = async ({
  entityId,
  paramsString,
}: IPeriodicTasks.IPeriodicOperationParamsProps): Promise<IPeriodicTasks.IPeriodicOperationListSchema> => {
  const { data } = await axiosClient.get(
    `/inventory/api/v2/periodic-operation/entity/${entityId}${paramsString}`
  );
  return data;
};

export const useGetPeriodicOperationTable = (
  params: IPeriodicTasks.IPeriodicOperationParamsProps
) => {
  return useQuery({
    queryKey: ['getPeriodicOperationTable', params],
    queryFn: () => getPeriodicOperationTable(params),
    enabled: !!params.entityId,
  });
};

/************************************ PERIODIC OPERATION DETAILS *********************************************/
export const getPeriodicOperationDetails = async ({
  entityId,
  operationId,
}: IPeriodicTasks.IGetTaskDetailsParams): Promise<IPeriodicTasks.IPeriodicTaskDetails> => {
  const { data } = await axiosClient.get(
    `/inventory/api/v1/periodic-operation/entity/${entityId}/operation/${operationId}`
  );
  return data;
};

export const useGetPeriodicOperationDetails = ({
  entityId,
  operationId,
}: IPeriodicTasks.IGetTaskDetailsParams) => {
  return useQuery({
    queryKey: ['getPeriodicOperationDetails', entityId, operationId],
    queryFn: () => getPeriodicOperationDetails({ entityId, operationId }),
    enabled: !!entityId && !!operationId,
  });
};

/********************************************************************************
 * *********************** CREATE PERIODIC OPERATION ****************************
 *********************************************************************************/

/************************************ CREATE PERIODIC OPERATION *********************************************/
export const createPeriodicOperation = async ({
  params,
  entityId,
}: {
  params: IPeriodicTasks.ICreateOperationSchema;
  entityId?: number;
}): Promise<IPeriodicTasks.ICreateOperationResponse> => {
  const { data } = await axiosClient.post(
    `/inventory/api/v1/periodic-operation/entity/${entityId}`,
    params
  );
  return data;
};
export const useCreatePeriodicOperation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['createPeriodicOperation'],
    mutationFn: createPeriodicOperation,
    onSuccess: () => {
      queryClient.invalidateQueries(['getOnboardingStatus']);
      queryClient.invalidateQueries(['getPeriodicOperationTable']);
      queryClient.invalidateQueries(['getPeriodicOperationWidgets']);
    },
  });
};
/************************************ GET CATEGORIES LIST *********************************************/
export const getCategoriesList =
  async (): Promise<IPeriodicTasks.IJobCategoriesListSchema> => {
    const { data } = await axiosClient.get(
      `/inventory/api/v2/job-categories/job-types?paginated=false`
    );
    return data;
  };
export const useGetCategoriesList = () => {
  return useQuery({
    queryKey: ['getCategoriesList'],
    queryFn: () => getCategoriesList(),
  });
};
/************************************ GET CAUSES LIST *********************************************/
export const getCausesList = async ({
  countryId,
  ...params
}: IPeriodicTasks.IGetCausesListParams): Promise<IPeriodicTasks.IGetCausesListResponse> => {
  const { data } = await axiosClient.get(
    `/inventory/api/v2/causes/${countryId}`,
    { params }
  );
  return data;
};

export const useGetCausesList = (
  params: IPeriodicTasks.IGetCausesListParams
) => {
  return useQuery({
    queryKey: ['getCausesList', params],
    queryFn: () => getCausesList(params),
    enabled: !!params.countryId,
  });
};
/************************************ GET PLOTS LIST *********************************************/

//  TODO: this is used elsewhere and should be shared
export const getPlotsList = async ({
  entityId,
  cropsIds,
}: IPeriodicTasks.IGetOperationPlotSchema): Promise<
  IPeriodicTasks.IOperationPlotSchema[]
> => {
  const { data } = await axiosClient.get(
    `/inventory/api/v1/entity-plots/${entityId}`,
    {
      params: {
        cropsIds,
      },
    }
  );
  return data;
};
export const useGetPlotsList = (
  params: IPeriodicTasks.IGetOperationPlotSchema
) => {
  return useQuery({
    queryKey: ['getPlotsList', params],
    queryFn: () => getPlotsList(params),
    enabled: !!params?.entityId,
  });
};

/************************************ GET PRODUCTS *********************************************/

const getProductsAcrossWarehouses = async ({
  entityId,
  ...params
}: IPeriodicTasks.IGetProductsAcrossWarehousesParams): Promise<IPeriodicTasks.IGetOperationProductsResponse> => {
  const { data } = await axiosClient.get(
    `/inventory/api/v1/warehouses-stock/products/${entityId}`,
    {
      params,
    }
  );
  return data;
};

export const useGetProductsAcrossWarehouses = (
  params: IPeriodicTasks.IGetProductsAcrossWarehousesParams
) => {
  return useInfiniteQuery({
    queryKey: ['getProductsAcrossWarehouses', params],
    queryFn: ({ pageParam }) =>
      getProductsAcrossWarehouses({
        ...params,
        page: pageParam ?? params.page,
      }),
    getNextPageParam: (lastPage) =>
      lastPage.currentPage < lastPage.totalPages
        ? lastPage.currentPage + 1
        : undefined,
    enabled: !!params.entityId,
  });
};

// get products by country

const getProductsByCountry = async ({
  countryCode,
  ...params
}: IPeriodicTasks.IGetProductsByCountryParams): Promise<IPeriodicTasks.IGetOperationProductsResponse> => {
  const { data } = await axiosClient.get(
    `/inventory/api/v1/products/periodic-operation/${countryCode}`,
    { params }
  );
  return data;
};

export const useGetProductsByCountry = (
  params: IPeriodicTasks.IGetProductsByCountryParams
) => {
  return useInfiniteQuery({
    queryKey: ['getProductsByCountry', params],
    queryFn: ({ pageParam }) =>
      getProductsByCountry({ ...params, page: pageParam ?? params.page }),
    getNextPageParam: (lastPage) =>
      lastPage.currentPage < lastPage.totalPages
        ? lastPage.currentPage + 1
        : undefined,
    enabled: !!params.countryCode && !!params.operationType,
  });
};

/************************************ Apply PERIODIC OPERATION  *********************************************/
const applyPeriodicOperation = async ({
  entityId,
  operationId,
}: IPeriodicTasks.IGetTaskDetailsParams): Promise<IPeriodicTasks.IApplyOperationResponse> => {
  const data = await axiosClient.patch(
    `/inventory/api/v1/periodic-operation/entity/${entityId}/operation/${operationId}/complete`
  );
  return data;
};

export const useApplyPeriodicOperation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: applyPeriodicOperation,
    onSuccess: () => {
      queryClient.invalidateQueries(['getPeriodicOperationTable']);
      queryClient.invalidateQueries(['getPeriodicOperationDetails']);
    },
  });
};

// get allowed operations

const getAllowedPeriodOperations = async (
  entityId: number
): Promise<IPeriodicTasks.IGetAllowedPeriodicOperationsResponse> => {
  const { data } = await axiosClient.get(
    `/inventory/api/v1/company-deposit-orders/periodic-oprations-checks/entity/${entityId}`
  );

  return data;
};

export const useGetAllowedPeriodOperations = (
  entityId: number,
  enabled?: boolean
) => {
  return useQuery({
    queryKey: ['getAllowedPeriodOperations', entityId],
    queryFn: () => getAllowedPeriodOperations(entityId),
    enabled: enabled && !!entityId,
  });
};
